/**
 * Profile.jsx — restructured Feb 28 2026 (user feedback: "I don't want
 * to see each profile mixed up within writer and reader. Once selected
 * reader profile only shows reader info and stats... the same for writer").
 *
 * Persona-scoped rendering:
 *   • READER view   → reader hero (streak) + reader buttons (no Earnings)
 *   • WRITER view   → writer hero (level, coins, GBP earnings) + writer
 *                     buttons (no Followed Authors)
 *   • ADMIN view    → admin hero + generic buttons (no Earnings, no
 *                     Followed Authors — admin metrics live on /admin-panel)
 *
 * Persona is resolved via `effectiveRoleFor(realRole, writerSpace)` so
 * admins peeking the app as reader/writer get the right scoped view
 * without losing god-mode access.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Crown, Shield, Edit2, Flame,
  BarChart3, Trophy, Bell, Lock, Coins, Settings as SettingsIcon, HelpCircle,
  Users as UsersIcon, ChevronRight, PenLine, BookOpen, TrendingUp,
  GraduationCap,
} from 'lucide-react';import { Button } from '../components/ui/button';
import { checkAuth, authFetch } from '../utils/authUtils';
import EditProfileDialog from '../components/EditProfileDialog';
import BankWidget from '../components/BankWidget';
import WriterApplicationCard from '../components/WriterApplicationCard';
import CrestBadge from '../components/CrestBadge';
import WriterStatsPanel from '../components/WriterStatsPanel';
import ReadingPaceTrend from '../components/ReadingPaceTrend';
import {
  effectiveRoleFor,
  getActiveSpace,
  getAdminViewingAs,
  onSpaceChange,
} from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Per-persona button manifests. The label + icon are stable across personas
// so users build muscle memory, only the items themselves are gated.
const READER_BUTTONS = [
  { key: 'progress',      label: 'Progress',          icon: TrendingUp,   accent: 'cyan',    to: '/progress',                 testid: 'profile-btn-progress' },
  { key: 'reader-journey', label: 'Journey to Top Reader', icon: Crown,   accent: 'emerald', to: '/reader-journey',           testid: 'profile-btn-reader-journey' },
  { key: 'stats',         label: 'My Stats',          icon: BarChart3,    accent: 'cyan',    to: '/profile/stats',            testid: 'profile-btn-stats' },
  { key: 'learn',         label: 'LEARN',             icon: GraduationCap, accent: 'cyan',   to: '/learn',                    testid: 'profile-btn-learn' },
  { key: 'achievements',  label: 'Achievements',      icon: Trophy,       accent: 'amber',   to: '/profile/achievements',     testid: 'profile-btn-achievements' },
  { key: 'followed',      label: 'Followed Authors',  icon: UsersIcon,    accent: 'emerald', to: '/profile/followed-authors', testid: 'profile-btn-followed' },
  { key: 'notifications', label: 'Notifications',     icon: Bell,         accent: 'amber',   to: '/profile/notifications',    testid: 'profile-btn-notifications' },
  { key: 'security',      label: 'Security',          icon: Lock,         accent: 'rose',    to: '/profile/security',         testid: 'profile-btn-security' },
  { key: 'help',          label: 'Help & how-tos',    icon: HelpCircle,   accent: 'cyan',    to: '/help',                     testid: 'profile-btn-help' },
  { key: 'account',       label: 'Account Settings',  icon: SettingsIcon, accent: 'slate',   to: '/profile/account',          testid: 'profile-btn-account' },
];

const WRITER_BUTTONS = [
  { key: 'progress',      label: 'Progress',          icon: TrendingUp,   accent: 'cyan',    to: '/progress',              testid: 'profile-btn-progress' },
  { key: 'journey',       label: 'Journey to Top Scribe', icon: Crown,    accent: 'amber',   to: '/writer-journey',        testid: 'profile-btn-journey' },
  { key: 'reader-journey', label: 'Journey to Top Reader', icon: Crown,   accent: 'emerald', to: '/reader-journey',        testid: 'profile-btn-reader-journey' },
  { key: 'stats',         label: 'My Stats',          icon: BarChart3,    accent: 'cyan',    to: '/profile/stats',         testid: 'profile-btn-stats' },
  { key: 'learn',         label: 'LEARN',             icon: GraduationCap, accent: 'cyan',   to: '/learn',                 testid: 'profile-btn-learn' },
  { key: 'achievements',  label: 'Achievements',      icon: Trophy,       accent: 'amber',   to: '/profile/achievements',  testid: 'profile-btn-achievements' },
  { key: 'earnings',      label: 'Earnings & Payout', icon: Coins,        accent: 'emerald', to: '/profile/earnings',      testid: 'profile-btn-earnings' },
  { key: 'notifications', label: 'Notifications',     icon: Bell,         accent: 'amber',   to: '/profile/notifications', testid: 'profile-btn-notifications' },
  { key: 'security',      label: 'Security',          icon: Lock,         accent: 'rose',    to: '/profile/security',      testid: 'profile-btn-security' },
  { key: 'help',          label: 'Help & how-tos',    icon: HelpCircle,   accent: 'cyan',    to: '/help',                  testid: 'profile-btn-help' },
  { key: 'account',       label: 'Account Settings',  icon: SettingsIcon, accent: 'slate',   to: '/profile/account',       testid: 'profile-btn-account' },
];

const ADMIN_BUTTONS = [
  { key: 'progress',      label: 'Progress',          icon: TrendingUp,   accent: 'cyan',    to: '/progress',              testid: 'profile-btn-progress' },
  { key: 'journey',       label: 'Journey to Top Scribe', icon: Crown,    accent: 'amber',   to: '/writer-journey',        testid: 'profile-btn-journey' },
  { key: 'reader-journey', label: 'Journey to Top Reader', icon: Crown,   accent: 'emerald', to: '/reader-journey',        testid: 'profile-btn-reader-journey' },
  { key: 'stats',         label: 'My Stats',          icon: BarChart3,    accent: 'cyan',    to: '/profile/stats',         testid: 'profile-btn-stats' },
  { key: 'learn',         label: 'LEARN',             icon: GraduationCap, accent: 'cyan',   to: '/learn',                 testid: 'profile-btn-learn' },
  { key: 'achievements',  label: 'Achievements',      icon: Trophy,       accent: 'amber',   to: '/profile/achievements',  testid: 'profile-btn-achievements' },
  { key: 'notifications', label: 'Notifications',     icon: Bell,         accent: 'amber',   to: '/profile/notifications', testid: 'profile-btn-notifications' },
  { key: 'security',      label: 'Security',          icon: Lock,         accent: 'rose',    to: '/profile/security',      testid: 'profile-btn-security' },
  { key: 'help',          label: 'Help & how-tos',    icon: HelpCircle,   accent: 'cyan',    to: '/help',                  testid: 'profile-btn-help' },
  { key: 'account',       label: 'Account Settings',  icon: SettingsIcon, accent: 'slate',   to: '/profile/account',       testid: 'profile-btn-account' },
];

// Co-admin gets the same surfaces as a reader PLUS read-only access to the
// admin panel + breakdown views. They don't see writer-only earnings or the
// "Journey to Top Scribe" CTA unless their pre_coadmin_role was writer (in
// which case the admin can also see the scribe space).
const COADMIN_BUTTONS = [
  { key: 'admin-panel',   label: 'Admin Panel (read-only)', icon: Shield,    accent: 'cyan',    to: '/admin',                testid: 'profile-btn-admin-panel' },
  { key: 'users-breakdown', label: 'Users Breakdown',       icon: UsersIcon, accent: 'cyan',    to: '/admin/breakdown/users', testid: 'profile-btn-users-breakdown' },
  { key: 'content-breakdown', label: 'Content Breakdown',   icon: BarChart3, accent: 'cyan',    to: '/admin/breakdown/content', testid: 'profile-btn-content-breakdown' },
  { key: 'stats',         label: 'My Stats',          icon: BarChart3,    accent: 'cyan',    to: '/profile/stats',         testid: 'profile-btn-stats' },
  { key: 'learn',         label: 'LEARN',             icon: GraduationCap, accent: 'cyan',   to: '/learn',                 testid: 'profile-btn-learn' },
  { key: 'achievements',  label: 'Achievements',      icon: Trophy,       accent: 'amber',   to: '/profile/achievements',  testid: 'profile-btn-achievements' },
  { key: 'notifications', label: 'Notifications',     icon: Bell,         accent: 'amber',   to: '/profile/notifications', testid: 'profile-btn-notifications' },
  { key: 'security',      label: 'Security',          icon: Lock,         accent: 'rose',    to: '/profile/security',      testid: 'profile-btn-security' },
  { key: 'help',          label: 'Help & how-tos',    icon: HelpCircle,   accent: 'cyan',    to: '/help',                  testid: 'profile-btn-help' },
  { key: 'account',       label: 'Account Settings',  icon: SettingsIcon, accent: 'slate',   to: '/profile/account',       testid: 'profile-btn-account' },
];

// Tailwind safelist — see AdminPanel.jsx for the same pattern. JIT only
// picks up literal class names, so we keep them in a static map.
const ACCENT_CLS = {
  cyan:    { bg: 'bg-cyan-500/15',    border: 'border-cyan-400/30',    text: 'text-cyan-300'    },
  amber:   { bg: 'bg-amber-500/15',   border: 'border-amber-400/30',   text: 'text-amber-300'   },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-400/30', text: 'text-emerald-300' },
  rose:    { bg: 'bg-rose-500/15',    border: 'border-rose-400/30',    text: 'text-rose-300'    },
  slate:   { bg: 'bg-slate-500/15',   border: 'border-slate-400/30',   text: 'text-slate-300'   },
};

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  // Keep server-calculated metrics distinct from unavailable data. A missing
  // response must never be presented as a zero-progress state.
  const [readerStats, setReaderStats] = useState(null);
  const [readerStatsError, setReaderStatsError] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [writerStats, setWriterStats] = useState(null); // {writerLevel, coinBalance, gbpBalance, totalCoinsEarned, followersCount, totalBooks}
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSeriesBreakdown, setShowSeriesBreakdown] = useState(false);
  const [seriesBreakdown, setSeriesBreakdown] = useState(null); // {series_count, total_minutes, per_series:[...]}
  const [seriesBreakdownError, setSeriesBreakdownError] = useState('');
  // Track viewing-as so the persona swap re-renders this page in real time.
  const [, setSpaceTick] = useState(0);

  useEffect(() => {
    const unsub = onSpaceChange(() => setSpaceTick((n) => n + 1));
    return unsub;
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setReaderStatsError('');
    setSubscriptionError('');
    try {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      setUser(u);

      // These are server-calculated aggregates. Preserve an unavailable state
      // rather than converting an omitted field or failed request to zero.
      try {
        const sr = await authFetch(`${BACKEND_URL}/api/stats`);
        if (!sr.ok) throw new Error(`HTTP ${sr.status}`);
        const s = await sr.json();
        setReaderStats({
          streak: typeof (s.reading_streak ?? s.current_streak) === 'number' ? (s.reading_streak ?? s.current_streak) : null,
          books_read: typeof s.books_read === 'number' ? s.books_read : null,
          minutes_listened: typeof (s.minutes_listened ?? s.total_minutes_listened) === 'number' ? (s.minutes_listened ?? s.total_minutes_listened) : null,
          series_count: typeof s.series_listened_count === 'number' ? s.series_listened_count : null,
        });
      } catch (error) {
        setReaderStats(null);
        setReaderStatsError('Reading and listening metrics are unavailable right now.');
      }

      // Subscription status is authoritative; no badge is shown until it loads.
      try {
        const sub = await authFetch(`${BACKEND_URL}/api/subscription/status`);
        if (!sub.ok) throw new Error(`HTTP ${sub.status}`);
        setSubscription(await sub.json());
      } catch (error) {
        setSubscription(null);
        setSubscriptionError('Membership status unavailable');
      }

      if (u.role === 'writer' || u.role === 'admin') {
        try {
          const wr = await authFetch(`${BACKEND_URL}/api/writer/stats`);
          if (!wr.ok) throw new Error(`HTTP ${wr.status}`);
          setWriterStats(await wr.json());
        } catch { setWriterStats(null); }
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Toggle the series breakdown panel — lazy-fetches /api/listening/by-series
  // on first open so we don't hammer the backend for users who never tap it.
  const toggleSeriesBreakdown = async () => {
    const next = !showSeriesBreakdown;
    setShowSeriesBreakdown(next);
    if (next && !seriesBreakdown) {
      setSeriesBreakdownError('');
      try {
        const r = await authFetch(`${BACKEND_URL}/api/listening/by-series`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setSeriesBreakdown(await r.json());
      } catch {
        setSeriesBreakdownError('Listening-by-series is unavailable right now.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-400">Loading profile...</p>
      </div>
    );
  }

  const isPremium = subscription?.subscription_tier === 'premium';
  const realRole = (user?.role || 'user').toLowerCase();
  const effective = effectiveRoleFor(realRole, getActiveSpace());
  // For admins, the bottom-nav viewing-as drives the persona; for writers
  // the scribeSpace already feeds effectiveRoleFor.
  const adminViewing = getAdminViewingAs();

  // Persona = the lens the user is currently using.
  //   real admin + viewing admin   → 'admin'
  //   real admin + viewing writer  → 'writer'
  //   real admin + viewing reader  → 'user'  (reader)
  //   real coadmin + viewing coadmin → 'coadmin'
  //   real coadmin + viewing reader/writer → matches effectiveRoleFor
  //   real writer (writer space)   → 'writer'
  //   real writer (reader space)   → 'user'
  //   real user                    → 'user'
  let persona = effective;
  if (realRole === 'admin' && adminViewing === 'admin') persona = 'admin';
  if ((realRole === 'coadmin' || realRole === 'king') && adminViewing === 'admin') persona = 'coadmin';

  // King-specific addition — append a Throne Room link so they can
  // appoint Ambassadors directly from their profile.
  const KING_EXTRAS = (realRole === 'king' || realRole === 'coadmin')
    ? [{ key: 'throne', label: 'Throne Room', icon: Crown, accent: 'amber', to: '/king', testid: 'profile-btn-throne-room' }]
    : [];

  const buttons =
    persona === 'admin'   ? ADMIN_BUTTONS   :
    persona === 'coadmin' ? [...KING_EXTRAS, ...COADMIN_BUTTONS] :
    persona === 'writer'  ? WRITER_BUTTONS  :
                            READER_BUTTONS;

  // Persona-specific badge + hero copy.
  const personaBadge =
    persona === 'admin'   ? { label: 'HIGH KING',    Icon: Crown,  cls: 'bg-red-600 text-white' } :
    persona === 'coadmin' ? { label: 'KING', Icon: Crown,  cls: 'bg-amber-600 text-white' } :
    persona === 'writer'  ? { label: 'WRITER',   Icon: PenLine, cls: 'bg-purple-600 text-white' } :
                            { label: 'READER',   Icon: BookOpen, cls: 'bg-blue-600 text-white' };

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="profile-page" data-persona={persona}>
      <header className="mb-6">
        <p className="eyebrow text-amber-300">
          {persona === 'admin'   ? 'High King space' :
           persona === 'coadmin' ? 'King space' :
           persona === 'writer'  ? 'Writer space'   :
                                   'Reader space'}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mt-1">
          {persona === 'admin'   ? 'High King Profile' :
           persona === 'coadmin' ? 'King Profile' :
           persona === 'writer'  ? 'Scribe Profile'   :
                                   'Reader Profile'}
          <span className="text-amber-300">.</span>
        </h1>
      </header>

      {/* Compact persona-scoped hero */}
      <section
        className="rounded-2xl bg-[#12131A] border border-white/8 p-5 sm:p-6 mb-6"
        data-testid="profile-hero"
      >
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-2 ring-indigo-500/30 flex-shrink-0"
              data-testid="profile-hero-avatar"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <User className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">{user?.name}</h2>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded-full flex items-center gap-1 tracking-widest ${personaBadge.cls}`}
                data-testid="profile-hero-persona-badge"
              >
                <personaBadge.Icon className="w-3 h-3" /> {personaBadge.label}
              </span>
              {isPremium && persona !== 'admin' && (
                <span className="px-2 py-0.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                  <Crown className="w-3 h-3" /> PREMIUM
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-400 min-w-0">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{user?.email}</span>
            </div>
            {subscriptionError && (
              <p className="mt-2 text-xs text-amber-200/80" role="status" data-testid="profile-membership-unavailable">
                {subscriptionError}
              </p>
            )}

            {/* Reader-only stat row — streak + books read + listened
                minutes (now clickable — opens per-series breakdown) */}
            {persona === 'user' && readerStats && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="profile-reader-stats">
                {typeof readerStats.streak === 'number' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/30">
                    <Flame className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-xs font-bold text-amber-200 tabular-nums">
                      {readerStats.streak} day{readerStats.streak === 1 ? '' : 's'}
                    </span>
                  </span>
                )}
                {typeof readerStats.books_read === 'number' && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/15 border border-blue-400/30"
                    data-testid="profile-books-read-pill"
                  >
                    <BookOpen className="w-3.5 h-3.5 text-blue-300" />
                    <span className="text-xs font-bold text-blue-200 tabular-nums">{readerStats.books_read}</span>
                    <span className="text-[10px] uppercase text-blue-300">read</span>
                  </span>
                )}
                {typeof readerStats.minutes_listened === 'number' && (
                  <button
                    type="button"
                    onClick={toggleSeriesBreakdown}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 hover:bg-emerald-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                    data-testid="profile-listened-pill"
                    aria-expanded={showSeriesBreakdown}
                    aria-controls="profile-series-breakdown"
                  >
                    <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
                    <span className="text-xs font-bold text-emerald-200 tabular-nums">{readerStats.minutes_listened}</span>
                    <span className="text-[10px] uppercase text-emerald-300">min</span>
                  </button>
                )}
                {typeof readerStats.series_count === 'number' && (
                  <button
                    type="button"
                    onClick={toggleSeriesBreakdown}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 hover:bg-fuchsia-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300"
                    data-testid="profile-series-count-pill"
                    aria-expanded={showSeriesBreakdown}
                    aria-controls="profile-series-breakdown"
                  >
                    <BarChart3 className="w-3.5 h-3.5 text-fuchsia-300" />
                    <span className="text-xs font-bold text-fuchsia-200 tabular-nums">{readerStats.series_count}</span>
                    <span className="text-[10px] uppercase text-fuchsia-300">series</span>
                  </button>
                )}
              </div>
            )}
            {persona === 'user' && readerStatsError && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-200/80" role="status" data-testid="profile-reader-stats-unavailable">
                <span>{readerStatsError}</span>
                <button type="button" onClick={fetchAll} className="font-bold underline underline-offset-2 hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 rounded">Retry</button>
              </div>
            )}

            {/* Scribe-only stat row — level + balance + lifetime earnings */}
            {persona === 'writer' && writerStats && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="profile-writer-stats">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-400/30">
                  <PenLine className="w-3.5 h-3.5 text-purple-300" />
                  <span className="text-xs font-bold text-purple-200 tabular-nums">{typeof writerStats.writerLevel === 'number' ? `L${writerStats.writerLevel}` : 'Level unavailable'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/30">
                  <Coins className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-xs font-bold text-amber-200 tabular-nums">{typeof writerStats.coinBalance === 'number' ? writerStats.coinBalance : '—'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30">
                  <span className="text-xs font-bold text-emerald-200 tabular-nums">{typeof writerStats.gbpBalance === 'number' ? `£${writerStats.gbpBalance.toFixed(2)}` : '—'}</span>
                  <span className="text-[10px] uppercase text-emerald-300">earned</span>
                </span>
              </div>
            )}

            {/* Admin-only stat row — kept minimal because /admin-panel is the
                dedicated dashboard. Just a "viewing as admin" reminder. */}
            {persona === 'admin' && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="profile-admin-stats">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-400/30">
                  <Shield className="w-3.5 h-3.5 text-red-300" />
                  <span className="text-xs font-bold text-red-200 tracking-widest uppercase">God Mode</span>
                </span>
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={() => setShowEditProfile(true)}
          className="w-full sm:w-auto mt-4 bg-indigo-600 hover:bg-indigo-700"
          data-testid="profile-edit-btn"
        >
          <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
        </Button>
      </section>

      {/* Reading Pace Trend — 14-day chart of drill sessions (readers).
          Iter 82: turns invisible practice into a visible growth loop. */}
      {persona === 'user' && (
        <section id="profile-pace-trend" className="mb-4" data-testid="profile-pace-trend">
          <ReadingPaceTrend days={14} />
        </section>
      )}

      {/* Per-series listening breakdown (Reader only) — toggled from the
          "X min" / "X series" pills above. Lazy-loaded on first open.
          Feb 28 2026 — user request: "NUMBER of Series listened to
          should be recoded as well as total mins listened, also broken
          down per series to show how the total adds up". */}
      {persona === 'user' && showSeriesBreakdown && (
        <section
          id="profile-series-breakdown"
          className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-4 mb-4"
          data-testid="profile-series-breakdown"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-white">
              Listening by Series
            </h2>
            <span className="text-xs text-gray-400">
              <span className="text-fuchsia-200 font-bold tabular-nums">
                {seriesBreakdown?.series_count ?? '…'}
              </span>{' '}
              series •{' '}
              <span className="text-emerald-200 font-bold tabular-nums">
                {seriesBreakdown?.total_minutes ?? '…'}
              </span>{' '}
              min total
            </span>
          </div>

          {seriesBreakdownError ? (
            <div className="py-3 text-center text-xs text-amber-200/80" role="status">
              <p>{seriesBreakdownError}</p>
              <button type="button" onClick={toggleSeriesBreakdown} className="mt-2 font-bold underline underline-offset-2 hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 rounded">Close and try again</button>
            </div>
          ) : !seriesBreakdown ? (
            <p className="text-xs text-gray-400 py-3 text-center">Loading…</p>
          ) : seriesBreakdown.per_series.length === 0 ? (
            <p
              className="text-xs text-gray-400 py-3 text-center"
              data-testid="profile-series-breakdown-empty"
            >
              No series listened to yet. Start playing an audiobook or cinematic
              series and your minutes will roll up here automatically.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="profile-series-breakdown-list">
              {seriesBreakdown.per_series.map((row) => (
                <li
                  key={row.series_id}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-900/50 border border-slate-700/40"
                  data-testid={`series-row-${row.series_id}`}
                >
                  {row.cover_image ? (
                    <img
                      src={row.cover_image}
                      alt=""
                      className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {row.title}
                    </p>
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider">
                      {row.type === 'cinematic' ? 'Cinematic' : 'Audio'} ·{' '}
                      {row.episodes_listened} ep
                      {row.episodes_listened === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p
                      className="text-base font-black text-emerald-200 tabular-nums leading-none"
                      data-testid={`series-row-${row.series_id}-min`}
                    >
                      {row.minutes}
                    </p>
                    <p className="text-[10px] uppercase text-gray-500">min</p>
                  </div>
                </li>
              ))}
              {seriesBreakdown.unaffiliated_minutes > 0 && (
                <li
                  className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-900/30 border border-slate-800/50 text-gray-400"
                  data-testid="series-row-unaffiliated"
                >
                  <div className="w-10 h-10 rounded-md bg-slate-800/50 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">Standalone TTS &amp; other</p>
                    <p className="text-[11px] uppercase tracking-wider">Not part of a series</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold tabular-nums leading-none">
                      {seriesBreakdown.unaffiliated_minutes}
                    </p>
                    <p className="text-[10px] uppercase text-gray-600">min</p>
                  </div>
                </li>
              )}
            </ul>
          )}
        </section>
      )}

      {/* Per-user coin bank — every earn + every spend, with a link to
          the full /bank ledger view. Iter 65. */}
      <div className="mt-6 mb-3 space-y-3" data-testid="profile-bank-widget-wrap">
        <CrestBadge />
        <WriterApplicationCard />
        <BankWidget />
      </div>

      {/* ── Persona-scoped detailed stats grid ──
          Feb 12 2026 user spec: "Wire all stats to look like this with
          appropriate stats to match each profile. Admin, reader,
          writers (Scribe 🖋️)." Mirrors the WriterStatsPanel layout
          but only shows sections that apply to the active persona. */}
      {user?.user_id && (
        <section
          className="mt-6 mb-6 rounded-2xl bg-[#12131A] border border-white/8 p-4 sm:p-5"
          data-testid="profile-detailed-stats"
        >
          <h2 className="text-sm uppercase tracking-widest font-bold text-slate-300 mb-3 flex items-center gap-2">
            {persona === 'admin'
              ? <><Shield className="w-4 h-4 text-violet-300" /> Admin Stats</>
              : persona === 'writer'
                ? <><PenLine className="w-4 h-4 text-amber-300" /> Scribe Stats 🖋️</>
                : <><BookOpen className="w-4 h-4 text-cyan-300" /> Reader Stats</>}
          </h2>
          <WriterStatsPanel
            userId={user.user_id}
            mode={
              persona === 'admin'   ? 'admin'  :
              persona === 'writer'  ? 'writer' :
              persona === 'coadmin' ? 'admin'  :
                                      'reader'
            }
          />
        </section>
      )}

      {/* Persona-scoped button grid */}
      <nav className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profile-button-grid">
        {buttons.map((b) => {
          const cls = ACCENT_CLS[b.accent] || ACCENT_CLS.slate;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => navigate(b.to)}
              className="group flex items-center gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-colors text-left"
              data-testid={b.testid}
            >
              <span className={`w-10 h-10 rounded-lg ${cls.bg} border ${cls.border} flex items-center justify-center flex-shrink-0`}>
                <b.icon className={`w-5 h-5 ${cls.text}`} />
              </span>
              <span className="flex-1 text-sm sm:text-base font-semibold text-white">{b.label}</span>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
            </button>
          );
        })}
      </nav>

      <EditProfileDialog
        open={showEditProfile}
        onOpenChange={setShowEditProfile}
        user={user}
        isWriter={persona === 'writer' || persona === 'admin'}
        onSaved={fetchAll}
      />
    </main>
  );
};

export default Profile;
